import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BookOpen,
  ExternalLink,
  FileText,
  Briefcase,
  Youtube,
  Globe,
  Search,
  Filter,
  Loader2,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resourceTypeIcons = {
  websites: Globe,
  blogs: FileText,
  youtube: Youtube,
  books: BookOpen,
  courses: Briefcase,
};

const resourceTypeColors = {
  websites: "text-blue-400 bg-blue-500/10",
  blogs: "text-purple-400 bg-purple-500/10",
  youtube: "text-red-400 bg-red-500/10",
  books: "text-emerald-400 bg-emerald-500/10",
  courses: "text-[#E8B923] bg-[#E8B923]/10",
};

const domainOptions = [
  "PM", "Consulting", "Finance IBCorporate Banking", "Finance PE",
  "Finance VCIBPE", "Data", "Marketing", "Sales", "Supply and Ops",
  "FOCOSGenManStrategy", "General"
];

const resourceTypeOptions = [
  { value: "websites", label: "Website" },
  { value: "blogs", label: "Blog" },
  { value: "youtube", label: "YouTube Channel" },
  { value: "books", label: "Book" },
  { value: "courses", label: "Course" },
];

// Helper function to render items as clickable links when they are URLs
const renderResource = (item, colorClass = "text-blue-400") => {
  if (item && (item.startsWith('http://') || item.startsWith('https://'))) {
    return (
      <a 
        href={item} 
        target="_blank" 
        rel="noopener noreferrer" 
        className={`${colorClass} hover:underline break-all`}
      >
        {item}
      </a>
    );
  }
  return <span className={colorClass}>{item}</span>;
};

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [counsellingMaterials, setCounsellingMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("domain");
  
  // Add resource modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    resource_type: "websites",
    domain: "General",
    file_url: "",
  });
  const [saving, setSaving] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchResources();
    fetchCounsellingMaterials();
  }, []);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/resources`);
      setResources(response.data.resources || []);
    } catch (error) {
      toast.error("Failed to fetch resources");
    } finally {
      setLoading(false);
    }
  };

  const fetchCounsellingMaterials = async () => {
    try {
      const response = await axios.get(`${API}/counselling`);
      setCounsellingMaterials(response.data.materials || []);
    } catch (error) {
      console.error("Failed to fetch counselling materials");
    }
  };

  // Add resource handlers
  const handleAddFormChange = (field, value) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddResource = async () => {
    if (!addForm.name.trim()) {
      toast.error("Resource name is required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/resources`, addForm);
      toast.success("Resource added successfully");
      setAddModalOpen(false);
      setAddForm({ name: "", resource_type: "websites", domain: "General", file_url: "" });
      fetchResources();
    } catch (error) {
      toast.error("Failed to add resource");
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers
  const openDeleteDialog = (resource) => {
    setResourceToDelete(resource);
    setDeleteDialogOpen(true);
  };

  const handleDeleteResource = async () => {
    if (!resourceToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/resources/${resourceToDelete.id}`);
      toast.success("Resource deleted successfully");
      setDeleteDialogOpen(false);
      setResourceToDelete(null);
      fetchResources();
    } catch (error) {
      toast.error("Failed to delete resource");
    } finally {
      setDeleting(false);
    }
  };

  // Filter resources
  const filteredResources = resources.filter((r) => {
    const matchesSearch = !search || 
      r.name?.toLowerCase().includes(search.toLowerCase()) ||
      r.domain?.toLowerCase().includes(search.toLowerCase());
    const matchesDomain = domainFilter === "all" || r.domain === domainFilter;
    return matchesSearch && matchesDomain;
  });

  // Group resources by domain
  const groupedByDomain = filteredResources.reduce((acc, r) => {
    const domain = r.domain || "General";
    if (!acc[domain]) acc[domain] = [];
    acc[domain].push(r);
    return acc;
  }, {});

  // Group resources by type
  const groupedByType = filteredResources.reduce((acc, r) => {
    const type = r.resource_type || "other";
    if (!acc[type]) acc[type] = [];
    acc[type].push(r);
    return acc;
  }, {});

  // Get unique domains from counselling materials
  const counsellingDomains = [...new Set(counsellingMaterials.map(m => m.domain))];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-manrope text-3xl font-bold text-gray-900">
            Resources Library
          </h1>
          <p className="text-gray-500 mt-1">
            Domain-specific counselling materials, resources, and guides
          </p>
        </div>
        <Button
          onClick={() => setAddModalOpen(true)}
          className="bg-[#E8B923] text-black hover:bg-[#D4A017]"
          data-testid="add-resource-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Resource
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search resources..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#E8B923]"
                data-testid="resource-search-input"
              />
            </div>
            <Select value={domainFilter} onValueChange={setDomainFilter}>
              <SelectTrigger className="w-full md:w-[200px] bg-gray-50 border-gray-200 text-gray-900">
                <Filter className="h-4 w-4 mr-2 text-gray-500" />
                <SelectValue placeholder="All Domains" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all" className="text-gray-900">All Domains</SelectItem>
                {domainOptions.map((domain) => (
                  <SelectItem key={domain} value={domain} className="text-gray-900">
                    {domain}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-gray-200 p-1.5 gap-1" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <TabsTrigger value="domain" className="data-[state=active]:bg-[#2C3E50] data-[state=active]:text-white data-[state=active]:shadow-sm text-gray-500 rounded-md px-4 py-2 text-sm font-medium transition-all">
            By Domain
          </TabsTrigger>
          <TabsTrigger value="type" className="data-[state=active]:bg-[#2C3E50] data-[state=active]:text-white data-[state=active]:shadow-sm text-gray-500 rounded-md px-4 py-2 text-sm font-medium transition-all">
            By Type
          </TabsTrigger>
          <TabsTrigger value="counselling" className="data-[state=active]:bg-[#2C3E50] data-[state=active]:text-white data-[state=active]:shadow-sm text-gray-500 rounded-md px-4 py-2 text-sm font-medium transition-all">
            Counselling Materials
          </TabsTrigger>
        </TabsList>

        {/* By Domain Tab */}
        <TabsContent value="domain" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-[#E8B923]" />
            </div>
          ) : Object.keys(groupedByDomain).length === 0 ? (
            <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent className="flex flex-col items-center justify-center h-64 text-gray-500">
                <BookOpen className="h-12 w-12 mb-3 opacity-50" />
                <p>No resources found</p>
                <Button
                  onClick={() => setAddModalOpen(true)}
                  variant="outline"
                  className="mt-4 border-[#E8B923] text-[#E8B923] hover:bg-[#E8B923]/10"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Resource
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedByDomain).map(([domain, domainResources]) => (
                <Card key={domain} className="bg-white border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-[#E8B923]" />
                      {domain}
                      <Badge className="ml-2 bg-gray-100 text-gray-500">
                        {domainResources.length} items
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {domainResources.map((resource) => {
                        const Icon = resourceTypeIcons[resource.resource_type] || FileText;
                        const colorClass = resourceTypeColors[resource.resource_type] || "text-gray-500 bg-gray-100/50";
                        return (
                          <div
                            key={resource.id}
                            className="p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-[#E8B923]/30 transition-all group"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className={`p-2 rounded-lg ${colorClass}`}>
                                  <Icon className="h-4 w-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  {resource.file_url ? (
                                    <a
                                      href={resource.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-blue-400 hover:underline font-medium truncate block"
                                    >
                                      {resource.name}
                                      <ExternalLink className="h-3 w-3 inline ml-1" />
                                    </a>
                                  ) : (
                                    <p className="text-sm text-gray-700 font-medium truncate">
                                      {resource.name}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-500 mt-1 capitalize">
                                    {resource.resource_type?.replace("_", " ")}
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-gray-500 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => openDeleteDialog(resource)}
                                data-testid={`delete-resource-${resource.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* By Type Tab */}
        <TabsContent value="type" className="mt-4">
          {Object.entries(groupedByType).map(([type, typeResources]) => {
            const Icon = resourceTypeIcons[type] || FileText;
            const colorClass = resourceTypeColors[type] || "text-gray-500 bg-gray-100/50";
            return (
              <Card key={type} className="bg-white border-gray-200 mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${colorClass}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="capitalize">{type.replace("_", " ")}s</span>
                    <Badge className="ml-2 bg-gray-100 text-gray-500">
                      {typeResources.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {typeResources.slice(0, 20).map((resource, i) => (
                      <div
                        key={i}
                        className="p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-[#E8B923]/30 transition-all group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1">
                            {resource.file_url ? (
                              <a 
                                href={resource.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-400 hover:underline truncate block"
                              >
                                {resource.name}
                              </a>
                            ) : (
                              <p className="text-sm text-gray-700 truncate">
                                {resource.name}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              {resource.domain}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100"
                            onClick={() => openDeleteDialog(resource)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Counselling Materials Tab */}
        <TabsContent value="counselling" className="mt-4">
          {counsellingMaterials.length === 0 ? (
            <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent className="flex flex-col items-center justify-center h-64 text-gray-500">
                <BookOpen className="h-12 w-12 mb-3 opacity-50" />
                <p>No counselling materials available.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {counsellingMaterials.map((material) => (
                <Card key={material.id} className="bg-white border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-[#E8B923]" />
                      {material.domain}
                      {material.target_role && (
                        <Badge className="ml-auto bg-[#E8B923]/20 text-[#E8B923]">
                          {material.target_role}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {material.cv_samples?.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">CV Samples</p>
                        <ul className="space-y-1">
                          {material.cv_samples.map((s, j) => (
                            <li key={j} className="text-xs flex items-center gap-1">
                              <FileText className="h-3 w-3 text-gray-500 flex-shrink-0" />
                              {renderResource(s, "text-gray-700")}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {material.skills_to_learn?.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Skills to Learn</p>
                        <div className="flex flex-wrap gap-1">
                          {material.skills_to_learn.slice(0, 5).map((s, j) => (
                            <Badge key={j} className="text-xs bg-emerald-500/20 text-emerald-400">
                              {s}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {material.books_to_read?.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Books</p>
                        <ul className="space-y-1">
                          {material.books_to_read.slice(0, 3).map((b, j) => (
                            <li key={j} className="text-xs flex items-center gap-1">
                              <BookOpen className="h-3 w-3 text-gray-500 flex-shrink-0" />
                              {renderResource(b, "text-gray-500")}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {material.websites_to_follow?.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Websites</p>
                        <ul className="space-y-1">
                          {material.websites_to_follow.slice(0, 3).map((w, j) => (
                            <li key={j} className="text-xs flex items-center gap-1">
                              <Globe className="h-3 w-3 text-blue-400 flex-shrink-0" />
                              {renderResource(w, "text-blue-400")}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {material.youtube_channels?.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">YouTube Channels</p>
                        <ul className="space-y-1">
                          {material.youtube_channels.slice(0, 3).map((y, j) => (
                            <li key={j} className="text-xs flex items-center gap-1">
                              <Youtube className="h-3 w-3 text-red-400 flex-shrink-0" />
                              {renderResource(y, "text-red-400")}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {material.courses_outside_mu?.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">External Courses</p>
                        <ul className="space-y-1">
                          {material.courses_outside_mu.slice(0, 3).map((c, j) => (
                            <li key={j} className="text-xs flex items-center gap-1">
                              <Briefcase className="h-3 w-3 text-[#E8B923] flex-shrink-0" />
                              {renderResource(c, "text-[#E8B923]")}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Resource Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Add Resource</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-500">Resource Name *</Label>
              <Input
                value={addForm.name}
                onChange={(e) => handleAddFormChange("name", e.target.value)}
                placeholder="e.g., Product Management Guide"
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-500">Type</Label>
                <Select 
                  value={addForm.resource_type} 
                  onValueChange={(value) => handleAddFormChange("resource_type", value)}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {resourceTypeOptions.map((type) => (
                      <SelectItem key={type.value} value={type.value} className="text-gray-900">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Domain</Label>
                <Select 
                  value={addForm.domain} 
                  onValueChange={(value) => handleAddFormChange("domain", value)}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {domainOptions.map((domain) => (
                      <SelectItem key={domain} value={domain} className="text-gray-900">
                        {domain}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">URL (optional)</Label>
              <Input
                value={addForm.file_url}
                onChange={(e) => handleAddFormChange("file_url", e.target.value)}
                placeholder="https://..."
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddModalOpen(false)}
              className="border-gray-200 text-gray-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddResource}
              disabled={saving}
              className="bg-[#E8B923] text-black hover:bg-[#D4A017]"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Resource
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Delete Resource</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Are you sure you want to delete <span className="text-gray-900 font-medium">"{resourceToDelete?.name}"</span>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-200 text-gray-700 hover:bg-gray-50">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteResource}
              disabled={deleting}
              className="bg-red-500 text-gray-900 hover:bg-red-600"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
